import { useState, useEffect, useMemo } from 'react';
import useRequestBadgesApi from '../../api/requests/useRequestBadgesApi';
import { PRIORITY_LEVELS } from '../../../constants/request.constant';

/**
 * useRequestBadges Hook
 *
 * Feature hook for managing request badge display state.
 * Fetches priority data and determines visibility.
 *
 * @param {Object} options
 * @param {string|number} options.requestProgressId - Request progress ID (for priority)
 *
 * @returns {Object} Badge data and display state
 */
const useRequestBadges = ({ requestProgressId, skipFetch = false }) => {
  const { getRequestPriority } = useRequestBadgesApi();

  const [priorityData, setPriorityData] = useState({
    priority: null,
    remainingDays: null,
    slaEnabled: false,
    requestId: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch priority data
  useEffect(() => {
    if (!requestProgressId || skipFetch) {
      setLoading(false);
      return;
    }

    const fetchPriority = async () => {
      setLoading(true);
      try {
        const data = await getRequestPriority(requestProgressId);
        setPriorityData(data);
      } catch (err) {
        console.error('Failed to fetch request priority:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPriority();
  }, [requestProgressId, getRequestPriority, skipFetch]);

  // Visible when we have a progress id and finished loading priority data
  const isVisible = useMemo(() => {
    if (skipFetch || !requestProgressId || loading) return false;
    return true;
  }, [requestProgressId, loading, skipFetch]);

  const priority = priorityData.priority;
  const capitalizedPriority =
    priority != null && priority !== ''
      ? priority.charAt(0).toUpperCase() + priority.slice(1)
      : '-';

  // Match source: zero-pad positive values (05, 49); keep actual overdue values
  const displayDays = useMemo(() => {
    if (loading || priorityData.remainingDays === null) return '--';
    if (typeof priorityData.remainingDays !== 'number') return '--';
    if (priorityData.remainingDays < 0) {
      return String(priorityData.remainingDays);
    }
    return priorityData.remainingDays.toString().padStart(2, '0');
  }, [loading, priorityData.remainingDays]);

  // Show days when SLA is enabled and produced a value; hide for low priority
  const showDays =
    priorityData.slaEnabled &&
    priorityData.remainingDays !== null &&
    priorityData.remainingDays !== undefined &&
    priority?.toLowerCase() !== PRIORITY_LEVELS.LOW;

  return {
    // Data
    priority,
    capitalizedPriority,
    remainingDays: priorityData.remainingDays,
    displayDays,
    requestId: priorityData.requestId,
    slaEnabled: priorityData.slaEnabled,

    // State
    loading,
    error,
    isVisible,
    showDays,
  };
};

export default useRequestBadges;
